import React from 'react';
import AIBadge from './AIBadge';
import { IconBell, IconBellOff, IconSparkle, IconStar } from './Icons';
import { isImageIcon } from './EntityIcon';
import { avatarImageSrc } from '../utils/avatar';
import './ParticipantsSidebar.css';

function ParticipantsSidebar({
  participants,
  aiParticipants,
  typingUsers,
  aiTyping,
  onlineUsers = {},
  aiPrefs = null,
  onToggleMuteAI,
  onSetPrimaryAI,
}) {
  const typingUserIds = new Set(typingUsers.map((u) => u.userId));
  const typingAiIds = new Set(aiTyping.map((a) => a.aiId));
  const mutedAiIds = new Set(aiPrefs?.mutedAiIds || []);
  // The effective primary: the user's stored choice when it matches an AI in
  // this room, otherwise the platform default (the first AI, i.e. Aimy).
  const primaryAiId =
    (aiPrefs?.primaryAiId && aiParticipants.find((a) => a.id === aiPrefs.primaryAiId)?.id) ||
    aiParticipants[0]?.id ||
    null;

  const getPresenceStatus = (userId) => {
    const entry = onlineUsers[userId];
    if (!entry) return 'offline';
    return entry.status || 'online';
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-section">
        <h3>People ({participants.length})</h3>
        <ul>
          {participants.map((p) => {
            const status = getPresenceStatus(p.id);
            return (
              <li key={p.id} className="participant-item">
                <span className="participant-presence-wrapper">
                  {p.avatar ? (
                    <span className="participant-avatar">
                      {isImageIcon(p.avatar) ? (
                        <img
                          className="participant-avatar-img"
                          src={avatarImageSrc(p.avatar)}
                          alt=""
                        />
                      ) : (
                        p.avatar
                      )}
                    </span>
                  ) : (
                    <span className="participant-dot" style={{ background: p.color }} />
                  )}
                  <span className={`presence-dot ${status}`} />
                </span>
                <span className="participant-name">{p.name}</span>
                {typingUserIds.has(p.id) && <span className="typing-label">typing...</span>}
                {!typingUserIds.has(p.id) && status === 'away' && (
                  <span className="away-label">away</span>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="sidebar-section">
        <h3>AI ({aiParticipants.length})</h3>
        <ul>
          {aiParticipants.map((ai) => {
            const muted = mutedAiIds.has(ai.id);
            const isPrimary = ai.id === primaryAiId;
            return (
              <li key={ai.id} className={`participant-item ai ${muted ? 'muted' : ''}`}>
                {(() => {
                  const sparkleColor = ai.color || '#d96f43';
                  const bgColor =
                    sparkleColor === '#127c6d' ? 'rgba(217,111,67,0.22)' : 'rgba(18,124,109,0.22)';
                  return (
                    <span
                      className="ai-sparkle-avatar"
                      style={{ color: sparkleColor, background: bgColor }}
                    >
                      <IconSparkle size={13} />
                    </span>
                  );
                })()}
                <span className="participant-name">{ai.name}</span>
                <AIBadge />
                {isPrimary && (
                  <span className="primary-ai-label" title={`${ai.name} answers generic asks`}>
                    primary
                  </span>
                )}
                {typingAiIds.has(ai.id) && <span className="typing-label">thinking...</span>}
                {muted && !typingAiIds.has(ai.id) && <span className="muted-label">muted</span>}
                <span className="ai-controls">
                  {onSetPrimaryAI && aiParticipants.length > 1 && (
                    <button
                      type="button"
                      className={`ai-control-btn ${isPrimary ? 'active' : ''}`}
                      onClick={() => onSetPrimaryAI(ai.id)}
                      disabled={isPrimary}
                      title={
                        isPrimary
                          ? `${ai.name} is your primary AI — it answers generic asks (@AI, questions)`
                          : `Make ${ai.name} your primary AI`
                      }
                      aria-label={
                        isPrimary ? `${ai.name} is your primary AI` : `Make ${ai.name} primary`
                      }
                    >
                      <IconStar size={13} fill={isPrimary ? 'currentColor' : 'none'} />
                    </button>
                  )}
                  {onToggleMuteAI && (
                    <button
                      type="button"
                      className={`ai-control-btn ${muted ? 'active' : ''}`}
                      onClick={() => onToggleMuteAI(ai.id)}
                      title={
                        muted
                          ? `Unmute ${ai.name}`
                          : `Mute ${ai.name} — stops unprompted replies to your messages (an explicit @${ai.name} still gets an answer)`
                      }
                      aria-label={muted ? `Unmute ${ai.name}` : `Mute ${ai.name}`}
                    >
                      {muted ? <IconBellOff size={13} /> : <IconBell size={13} />}
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="sidebar-section sidebar-help">
        <h3>Tips</h3>
        <ul className="tips-list">
          <li>
            End with <code>?</code> to trigger AI
          </li>
          <li>
            Type <code>@AI</code> for the default AI, or call one by name (e.g.{' '}
            {aiParticipants.map((ai, i) => (
              <React.Fragment key={ai.id}>
                {i > 0 ? ', ' : ''}
                <code>@{ai.name}</code>
              </React.Fragment>
            ))}
            )
          </li>
          <li>
            Click <strong>Ask AI</strong> to invoke directly
          </li>
        </ul>
      </div>
    </aside>
  );
}

export default ParticipantsSidebar;
